import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, Loader2, Mail, Clock, ShieldCheck, ArrowLeft, Sparkles, CalendarClock } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { calendlyLinkProps } from '../lib/calendly';

const perks = [
    { icon: Clock, title: 'Reply within 24 hours', desc: 'A real engineer reviews your request — no chatbots, no runaround.' },
    { icon: ShieldCheck, title: 'Free automation audit', desc: 'We map your biggest time-sinks and where automation pays off fastest.' },
    { icon: Sparkles, title: 'No obligation', desc: "You'll leave the call with a clear plan whether we work together or not." },
];

const inputClass =
    'w-full px-4 py-3 rounded-xl bg-navy-900 border border-white/[0.1] text-white placeholder:text-text-faint focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan outline-none transition-all';

const Contact: React.FC = () => {
    const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    useEffect(() => {
        emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '');
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        try {
            await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
                {
                    from_name: formData.name,
                    from_email: formData.email,
                    company: formData.company,
                    phone: formData.phone,
                    message: formData.message,
                }
            );
            setStatus('success');
            setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        } catch (error) {
            console.error('EmailJS Error:', error);
            setStatus('error');
        }
    };

    return (
        <section id="contact" className="relative py-24 md:py-32 overflow-hidden">
            {/* backdrop */}
            <div className="absolute inset-0 bg-grid-fade [mask-image:radial-gradient(ellipse_70%_60%_at_50%_100%,#000_30%,transparent_100%)] pointer-events-none" />
            <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-brand-blue/15 rounded-full blur-[140px] opacity-40 pointer-events-none" />

            <div className="container-x relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left: pitch */}
                    <div>
                        <span className="eyebrow mb-4">
                            <span className="h-px w-6 bg-brand-cyan/60" />
                            Let's talk
                        </span>
                        <h2 className="font-display text-3xl md:text-[2.75rem] md:leading-[1.1] font-bold text-white tracking-tight text-balance">
                            Ready to put your business <span className="text-gradient">on autopilot?</span>
                        </h2>
                        <p className="mt-5 text-lg text-text-muted leading-relaxed max-w-lg">
                            Book a free consultation and we'll show you exactly where automation can save you the
                            most time and money — with a concrete plan to get there.
                        </p>

                        <div className="mt-10 space-y-5">
                            {perks.map((perk) => (
                                <div key={perk.title} className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 border border-brand-cyan/20 grid place-items-center shrink-0">
                                        <perk.icon className="w-5 h-5 text-brand-cyan" />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold">{perk.title}</div>
                                        <div className="text-sm text-text-muted">{perk.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                            <a {...calendlyLinkProps} className="btn-primary group">
                                Schedule a call
                                <CalendarClock className="w-4 h-4 group-hover:rotate-6 transition-transform" />
                            </a>
                            <a
                                href="mailto:info@automateit.ng"
                                className="inline-flex items-center gap-2 text-text-muted hover:text-brand-cyan transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                info@automateit.ng
                            </a>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="rounded-3xl glass shadow-lift p-6 md:p-8">
                        {status === 'success' ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                                </div>
                                <h3 className="font-display text-2xl font-bold text-white mb-2">Message received!</h3>
                                <p className="text-text-muted">Thanks for reaching out — we'll get back to you within 24 hours.</p>
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="mt-8 inline-flex items-center gap-2 text-brand-cyan font-semibold hover:text-white transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Send another message
                                </button>
                            </div>
                        ) : status === 'error' ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                                    </svg>
                                </div>
                                <h3 className="font-display text-2xl font-bold text-white mb-2">Something went wrong</h3>
                                <p className="text-text-muted mb-4">
                                    We couldn't send your message. Please try again or email us directly at{' '}
                                    <a href="mailto:info@automateit.ng" className="text-brand-cyan">hello@automateit.ng</a>.
                                </p>
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="mt-2 text-brand-cyan font-semibold hover:text-white transition-colors"
                                >
                                    Try again
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label htmlFor="name" className="text-sm font-medium text-text-muted">Full name</label>
                                        <input id="name" name="name" type="text" required autoComplete="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="John Doe" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label htmlFor="email" className="text-sm font-medium text-text-muted">Work email</label>
                                        <input id="email" name="email" type="email" required autoComplete="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="john@company.com" />
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label htmlFor="company" className="text-sm font-medium text-text-muted">Company</label>
                                        <input id="company" name="company" type="text" required autoComplete="organization" value={formData.company} onChange={handleChange} className={inputClass} placeholder="Acme Inc." />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label htmlFor="phone" className="text-sm font-medium text-text-muted">Phone <span className="text-text-faint">(optional)</span></label>
                                        <input id="phone" name="phone" type="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="+234..." />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label htmlFor="message" className="text-sm font-medium text-text-muted">What would you like to automate?</label>
                                    <textarea id="message" name="message" required rows={4} value={formData.message} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Tell us about the manual processes eating up your team's time..." />
                                </div>

                                <button type="submit" disabled={status === 'submitting'} className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0">
                                    {status === 'submitting' ? (
                                        <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>
                                    ) : (
                                        <>Request my free audit <Send className="w-4 h-4" /></>
                                    )}
                                </button>
                                <p className="text-center text-xs text-text-faint">We'll never share your details. No spam, ever.</p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
